import React, { useMemo, useState } from 'react'
import { QueryResult } from 'models/query-result.model'
import { WordTranslationLine } from 'lib/interfaces'
import { buildTranslationViewModel } from 'lib/translation-view-model'
import { useTranslit } from 'hooks/useTranslit'
import FullTranslation from './full-translation'
import SentenceRail from './sentence-rail'
import WordBreakdownCard from './word-breakdown-card'

type Props = {
   lines: WordTranslationLine[]
   queryResult: QueryResult
}

/**
 * The redesigned translation results: the full translation up top (clamped
 * when long), then a sentence rail beside a word-breakdown card that shows
 * one active sentence at a time.
 */
const TranslationView = ({ lines, queryResult }: Props) => {
   const model = useMemo(() => buildTranslationViewModel(lines), [lines])
   const [activeIndex, setActiveIndex] = useState(0)
   const [expanded, setExpanded] = useState(false)

   // A new result starts back at the first sentence, collapsed. Reset during
   // render (not in an effect) so the new lines never paint one frame paired
   // with the previous query's selection.
   const [resultFor, setResultFor] = useState(queryResult)
   if (resultFor !== queryResult) {
      setResultFor(queryResult)
      setActiveIndex(0)
      setExpanded(false)
   }

   const translit = useTranslit()

   const total = model.sentences.length
   if (!total) return null

   const active = model.sentences[Math.min(activeIndex, total - 1)]

   // Selecting inside the full text also opens the clamp, so the highlight is
   // never cut off; the rail keeps the clamp as-is.
   const selectFromText = (index: number) => {
      setActiveIndex(index)
      setExpanded(true)
   }

   return (
      <div>
         <FullTranslation
            model={model}
            activeIndex={active.index}
            expanded={expanded}
            onSelectSentence={selectFromText}
            onToggleExpanded={() => setExpanded(!expanded)}
            translit={translit}
         />

         <section className="flex items-start gap-6 mt-[30px]">
            {total > 1 && (
               <SentenceRail
                  model={model}
                  activeIndex={active.index}
                  onSelect={setActiveIndex}
                  translit={translit}
               />
            )}
            <div className="flex-1 min-w-0">
               {/* Desktop shows the rail-selected sentence; mobile has no rail,
                   so every sentence's breakdown stacks in one card. */}
               <div className="max-md:hidden">
                  <WordBreakdownCard
                     units={[active]}
                     unitNoun={model.unitNoun}
                     translit={translit}
                  />
               </div>
               <div className="md:hidden">
                  <WordBreakdownCard
                     units={model.sentences}
                     unitNoun={model.unitNoun}
                     translit={translit}
                  />
               </div>
            </div>
         </section>
      </div>
   )
}

export default TranslationView
